import { type AgentConfigOptions, apm, type ApmBase } from "@elastic/apm-rum";
import {
  catchFailure,
  mapAsyncSpread,
  mapSpread,
  pipe,
  succeed,
  tap,
} from "@otto-ec/assets-core-utils/nano-effect";
import * as toggle from "../../exports/toggle";
import { assignments } from "../../exports/onex";
import { sendCustomError } from "./async/sendCustomError";
import {
  computeApmActivationRequirements,
  getApmInitializationConfiguration,
  TOGGLE_ADD_ONEX_DATA_TO_APM_CONTEXT,
  TOGGLE_DROP_UNSAMPLED_DISABLE,
  TOGGLE_ENRICH_ERRORS_DISABLE,
  TOGGLE_ENRICH_SPAN_DATA,
} from "./async/config";
import { type NoOps, withTransaction } from "./async/utils";
import {
  dropSpanDataFromUnsampledPageload,
  dropTransactionsByFilter,
  enrichErrors,
} from "./async/filters";
import { aggregateResourceMetrics, enrichResourceSpans } from "./async/resource";
import { captureError } from "./async/captureError";

/**
 *
 */
const initializeApmAgentIfRequirementsMet = mapAsyncSpread(
  async (config: AgentConfigOptions, labels: Labels, pageLoadSamplingActive: boolean) => {
    /*                                                */

    /*                                                         */
    apm.init({ ...config, active: config.active ?? true });

    /*                                                    */
    apm.addLabels(labels);
    apm.addLabels({ page_merge_id: (await otto.tracking.getPageMergeId()) ?? "unknown" });

    (otto.apps.global.appVersion() as Promise<string | undefined>).then((appVersion) => {
      if (appVersion) apm.addLabels({ appVersion });
    });

    /*                                        */
    if (!toggle.get(TOGGLE_DROP_UNSAMPLED_DISABLE, false)) apm.addFilter(dropTransactionsByFilter);

    /*                                                              */
    if (!toggle.get(TOGGLE_DROP_UNSAMPLED_DISABLE, false))
      apm.addFilter(dropSpanDataFromUnsampledPageload(pageLoadSamplingActive));

    /*                                            */
    if (!toggle.get(TOGGLE_ENRICH_ERRORS_DISABLE, false)) apm.addFilter(enrichErrors);

    /*                                     */
    if (toggle.get(TOGGLE_ADD_ONEX_DATA_TO_APM_CONTEXT, true))
      apm.setCustomContext({ onex: { assignments: assignments.get() } });

    /*                                                                  */
    if (toggle.get(TOGGLE_ENRICH_SPAN_DATA, false)) {
      apm.addFilter(enrichResourceSpans);
      apm.addFilter(aggregateResourceMetrics);
    }

    return [apm, pageLoadSamplingActive] as const;
  },
);

/**
 *
 *
 */
const createNexusNamespaceWithAgentEnabled = mapSpread(
  (agent: ApmBase, pageLoadSamplingActive: boolean) =>
    ({
      agent: () => agent,
      startSpan: (...a) => agent.startSpan(...a),
      captureError,
      mark: withTransaction(agent, (t, ...a) => t.mark(...a)),
      addLabels: withTransaction(agent, (t, ...a) => t.addLabels(...a)),
      getCurrentTransaction: () => agent.getCurrentTransaction(),
      sendCustomError,
      isActive: () => pageLoadSamplingActive,
    }) satisfies Parameters<typeof otto.apm.assignNamespace>[0],
);

/**
 *
 *
 */
const createNexusNamespaceWithAgentDisabled = catchFailure(([noopNull, noopUndefined]: NoOps) =>
  succeed({
    agent: noopNull,
    startSpan: noopUndefined,
    captureError: noopNull,
    mark: noopUndefined,
    addLabels: noopUndefined,
    getCurrentTransaction: noopUndefined,
    sendCustomError: noopNull,
    isActive: () => false,
  } satisfies Parameters<typeof otto.apm.assignNamespace>[0]),
);

/**
 *
 *
 */
const assignNexusNamespaceWithTestingLock = tap(
  (nsInput: Parameters<typeof otto.apm.assignNamespace>[0]) =>
    otto.apm.assignNamespace(nsInput, { lock: !import.meta.env.TEST }),
);

/*                                            */
pipe(
  getApmInitializationConfiguration(),
  computeApmActivationRequirements,
  initializeApmAgentIfRequirementsMet,
  createNexusNamespaceWithAgentEnabled,
  createNexusNamespaceWithAgentDisabled,
  assignNexusNamespaceWithTestingLock,
);
